import axios from "axios"
import { getServiceToken } from '../utils/encryption';
import { markServiceAvailable, markServiceUnavailable } from '../utils/service-status';


// Create API instance
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true  // send httpOnly auth cookie automatically
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            // Proactively refresh token if it expires within 2 seconds.
            // Skip for /options/ itself to avoid an infinite refresh loop.
            const expiresAt = parseInt(localStorage.getItem('tokenExpiresAt') || '0', 10);
            const isOptionsCall = config.url?.includes('/options/');
            const isExpiringSoon = !isOptionsCall && expiresAt && (expiresAt - Date.now() < 2 * 1000);
            if (isExpiringSoon) {
                try {
                    const { fetchAuth } = await import('../utils/auth');
                    await fetchAuth();
                } catch (_) {
                    // silent — response interceptor will handle 403 if it still fails
                }
            }

            // Get fresh token
            const token = localStorage.getItem('token');
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }

            // Add a rotating service token (changes every 30s) so the proxy
            // knows the request came from the React app
            const serviceToken = getServiceToken();
            if (config.url?.includes('/image/') || config.url?.includes('/stream-audio/')) {
                const fullUrl = new URL(config.url, process.env.REACT_APP_API_URL);
                fullUrl.searchParams.set('x-service-token', serviceToken);
                config.url = fullUrl.toString();
            } else {
                config.headers['x-service-token'] = serviceToken;
            }

            return config;
        } catch (error) {
            console.error('Request interceptor error:', error);
            return Promise.reject(error);
        }
    },
    (error) => Promise.reject(error)
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        markServiceAvailable();
        return response;
    },
    async (error) => {
        const config = error.config;
        config.retryCount = config.retryCount || 0;

        if (!error.response) {
            markServiceUnavailable('Proxy or API server is unavailable. Please try again later.');
            return Promise.reject(error);
        }

        if (error.response.status === 429) {
            const retryAfter = error.response.headers?.['retry-after'];
            const wait = retryAfter ? ` Try again in ${retryAfter} seconds.` : ' Please slow down and try again shortly.';
            markServiceUnavailable(`Too many requests.${wait}`);
        }

        if (error.response.status >= 500) {
            markServiceUnavailable('Service is temporarily unavailable. Please try again in a moment.');
        }

        if ((error.response?.status === 401 || error.response?.status === 403) && config.retryCount < 2) {
            config.retryCount += 1;
            console.log(`Retry attempt ${config.retryCount} for ${config.url}`);

            try {
                const { fetchAuth } = await import('../utils/auth');
                await fetchAuth();
                const token = localStorage.getItem('token');
                
                if (!token) {
                    throw new Error('Token refresh failed');
                }

                // Preserve all headers while updating auth token
                config.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(config);
            } catch (refreshError) {
                console.error('Auth refresh failed:', refreshError);
                if (!refreshError.response) {
                    markServiceUnavailable('Authentication service is unavailable. Please try again shortly.');
                }
                if (config.retryCount >= 2) {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export { axiosInstance as API };